<template>
  <el-date-picker
    v-model="dateRange"
    :type="type"
    :picker-options="type === 'daterange' ? pickerOptionData : {}"
    range-separator=" ~ "
    start-placeholder="开始日期"
    end-placeholder="结束日期"
    :clearable="clearable"
    class="date-range-search"
    :default-time="['00:00:00', '23:59:59']"
    :popper-class="popperClass"
    @change="handleDateRangeChange"
  >
  </el-date-picker>
</template>

<script>
import nativeMsgUtil from '@/utils/native_msg_util'

export default {
  props: {
    // v-model 的定义
    value: {
      type: Array,
      default: () => null
    },
    clearable: {
      type: Boolean,
      default: true
    },
    // 拓展年选项
    extendDefaultYearOptions: {
      type: Boolean,
      default: true
    },
    extraPickerOptions: {
      type: Array,
      default: () => []
    },
    pickerOptions: {
      type: Object,
      default: () => {}
    },
    /**
     * 时间范围器筛选类型（目前仅支持daterange/monthrange）
     *
     */
    type: {
      type: String,
      default: 'daterange'
    }
  },
  data() {
    return {
      dateRange: null,
      popperClass: '',
      defaultExtraPickerOptions: [
        {
          text: '近3个月',
          onClick(picker) {
            const end = new Date()
            const start = moment(new Date()).subtract(3, 'months')
            picker.$emit('pick', [start, end])
          }
        },
        {
          text: '近6个月',
          onClick(picker) {
            const end = new Date()
            const start = moment(new Date()).subtract(6, 'months')
            picker.$emit('pick', [start, end])
          }
        },
        {
          text: '近12个月',
          onClick(picker) {
            const end = new Date()
            const start = moment(new Date()).subtract(1, 'years')
            picker.$emit('pick', [start, end])
          }
        },
        {
          text: `${new Date().getFullYear()}年`,
          onClick(picker) {
            const end = moment(new Date()).endOf('year')
            const start = moment(new Date()).startOf('year')
            picker.$emit('pick', [start, end])
          }
        },
        {
          text: `${new Date().getFullYear() - 1}年`,
          onClick(picker) {
            const end = moment(new Date())
              .subtract(1, 'years')
              .endOf('year')
            const start = moment(new Date())
              .subtract(1, 'years')
              .startOf('year')
            picker.$emit('pick', [start, end])
          }
        }
      ]
    }
  },
  computed: {
    /**
     * 时间采集的配置
     *
     * @returns {object} 选项
     */
    pickerOptionData() {
      return {
        // 快捷时间的格式内容
        shortcuts: [...this.baseOptions(), ...this.latestMultiMonthsOptions(), ...this.yearPickerOptions],
        // 其他配置
        ...this.pickerOptions
      }
    },
    yearPickerOptions() {
      if (this.extendDefaultYearOptions) {
        return this.defaultExtraPickerOptions
      }

      return this.extraPickerOptions
    }
  },
  watch: {
    value() {
      // 当 v-model 变化后，更新 picker 的显示
      this.dateRange = this.value
    }
  },
  created() {
    // 初始化值
    this.dateRange = this.value

    // 如果是手机端下，则设置兼容
    if (nativeMsgUtil.isUnderMobile()) {
      this.popperClass = 'lc-date-range-picker-mobile-popper'
    }
  },
  methods: {
    /**
     * 获取基本的时间选项
     *
     * @returns {Array} 时间选项
     */
    baseOptions() {
      let self = this
      return [
        {
          text: '今天',
          onClick(picker) {
            const end = self.$moment().endOf('day')
            const start = self.$moment().startOf('day')
            picker.$emit('pick', [start, end])
          }
        },
        {
          text: '昨天',
          onClick(picker) {
            const end = self
              .$moment(new Date())
              .add(-1, 'days')
              .endOf('day')
            const start = self
              .$moment(new Date())
              .add(-1, 'days')
              .startOf('day')
            picker.$emit('pick', [start, end])
          }
        },
        {
          text: '最近7天',
          onClick(picker) {
            const end = self.$moment().endOf('day')
            const start = self
              .$moment(new Date())
              .add(-6, 'days')
              .startOf('day')
            picker.$emit('pick', [start, end])
          }
        },
        {
          text: '最近30天',
          onClick(picker) {
            // 统一格式，秒后面的毫秒去除 0.999
            const end = self.$moment().endOf('day')
            const start = self
              .$moment(new Date())
              .add(-29, 'days')
              .startOf('day')
            picker.$emit('pick', [start, end])
          }
        }
      ]
    },
    /**
     * 获取最近6月的时间选项
     *
     * @returns {Array} 时间选项
     */
    latestMultiMonthsOptions() {
      let beginMonth = this.$moment().startOf('month')
      let endMonth = this.$moment().endOf('month')

      let array = [0, 1]
      return array.map(index => {
        let newBeginMonth = beginMonth.clone().subtract(index, 'months')
        let newEndMonth = endMonth
          .clone()
          .subtract(index, 'months')
          .endOf('month')

        let dateText = newBeginMonth.format('YY年 M月')

        return {
          text: dateText,
          onClick(picker) {
            picker.$emit('pick', [newBeginMonth.toDate(), newEndMonth.toDate()])
          }
        }
      })
    },
    /**
     * 选项改变后
     */
    handleDateRangeChange() {
      if (!this.isEmptyObject(this.dateRange)) {
        // 修正手动改日期时，会自动变为获取 00:00:00 的时间问题
        if (this.type === 'monthrange') {
          this.dateRange[1] = moment(this.dateRange[1])
            .endOf('month')
            .milliseconds(0)
            .toDate()
        } else {
          this.dateRange[1] = moment(this.dateRange[1])
            .endOf('day')
            .milliseconds(0)
            .toDate()
        }
      }
      this.$emit('input', this.dateRange)
      this.$emit('change', this.dateRange)
    }
  }
}
</script>

<style lang="scss" scoped>
.el-range-editor--small .el-range-input {
  width: 72px;
}

.el-range-editor--small.el-input__inner {
  width: 210px;
}
.el-range-editor--small {
  ::v-deep .el-range-input {
    font-size: 12px;
    font-family: Arial;
  }
}
::v-deep .el-range-separator {
  padding-left: 0px;
  width: 20px !important;
}
</style>

<style lang="scss">
// 兼容手机端
.lc-date-range-picker-mobile-popper {
  width: 350px !important;
  overflow-x: auto;
}

// .el-date-range-picker .el-picker-panel__body {
//   width: 500px;
//   min-width: 500px;
// }
</style>
